import { ThemeConfig } from '@nylas/core';
import { Component, h, Prop, State, Event, EventEmitter, Watch, Element } from '@stencil/core';

/**
 * The `toggle-switch` component is a UI component that allows users to toggle a switch.
 * This component is used in the scheduling form to input toggle switch type inputs.
 * Use it as a replacement for checkboxes when you want to present a switch in the UI.
 *
 * @part ts_label - The label of the toggle switch
 * @part ts_input - The input of the toggle switch
 * @part ts_slider - The slider of the toggle switch
 */
@Component({
  tag: 'toggle-switch',
  styleUrl: 'toggle-switch.scss',
  shadow: true,
})
export class ToggleSwitchComponent {
  @Element() el!: HTMLElement;
  /**
   * The name of the toggle switch. This is used to identify the toggle switch when submitting a form.
   */
  @Prop() name: string = 'toggle-switch';
  /**
   * The default value of the toggle switch. This is the value that is displayed when the toggle switch is rendered.
   */
  @Prop() checked: boolean = false;
  /**
   * The label of the toggle switch. This is displayed next to the toggle switch.
   */
  @Prop() label: string = '';
  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The checked state of the toggle switch.
   */
  @State() isChecked: boolean = this.checked;

  /**
   * This event is fired when the toggle switch is toggled.
   */
  @Event() nylasFormSwitchToggled!: EventEmitter<{
    checked: boolean;
    name: string;
    label: string;
  }>;

  @Watch('checked')
  checkedChanged(newValue: boolean) {
    this.isChecked = newValue;
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  // Lifecycle events
  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    this.isChecked = this.checked;
  }

  componentDidLoad() {
    // Theme already applied in connectedCallback
  }

  toggleCheck() {
    this.isChecked = !this.isChecked;
    this.nylasFormSwitchToggled.emit({
      checked: this.isChecked,
      name: this.name,
      label: this.label,
    });
  }

  render() {
    return (
      <label class="switch" part="ts_label">
        <input type="checkbox" part="ts_input" name={this.name} checked={this.isChecked} onChange={() => this.toggleCheck()} />
        <div class="slider round" part={`ts_slider ${this.isChecked ? 'ts_slider_checked' : ''}`}></div>
      </label>
    );
  }
}
